/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { Box, Portal, useMediaQuery } from '@mui/material';
import { HotkeysProvider } from '@tanstack/react-hotkeys';
import { useQueryClient } from '@tanstack/react-query';
import {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '../auth';
import {
  setDesktopResizing,
  setReservedRightVar,
  useDesktopLayout,
} from '../desktop/desktopLayout';
import { releaseAgentPanelKeyboard } from '../desktop/hotkeys/agentPanelKeyboard';
import { AGENT_HOTKEY_SCOPE } from '../desktop/hotkeys/appHotkeys';
import { DesktopShellRouter } from '../desktop/navigation/DesktopShellRouter';
import { useShellNavigation } from '../desktop/navigation/useShellNavigation';
import { useUserPreferences } from '../desktop/preferences/UserPreferencesContext';
import { lazy } from '../lazy';
import { useLocalStorage } from '../react-components/useLocalStorage';
import { AgentPanelContext } from './AgentPanelContext';
import { AGENT_CONVERSATION_ID_STORAGE_KEY, claimAgentTabSession } from './agentTabSession';
import type { AgentExperienceHandle } from './components/AgentExperience';
import { useAgentSurface } from './components/hooks/useAgentSurface';
import { useHorizontalResize } from './components/hooks/useHorizontalResize';
import { getLastAgentActivity, isRecentActivity, markAgentActivity } from './conversationRecency';
import AgentLauncherChip from './launcher/AgentLauncherChip';
import { AgentLauncherContext } from './launcher/AgentLauncherContext';
import { useAgentLauncherStatus } from './launcher/launcherStatus';
import { getAgentPageContext } from './pageContext';
import type { AgentDraftSeed } from './types';
import { useAgentOnlyLayout } from './useAgentOnlyLayout';
import { useAgentShortcuts } from './useAgentShortcuts';
import AgentDesktopBridge from './workspace/AgentDesktopBridge';
import { AgentWorkspaceContext } from './workspace/AgentWorkspaceContext';
import { useAgentWorkspaceValue } from './workspace/useAgentWorkspaceValue';

const AgentSidePanel = lazy(() => import('./AgentSidePanel.js'));
const QuickFind = lazy(() => import('./quick-find/QuickFind.js'));

const AGENT_PANEL_DESKTOP_MIN_WIDTH = 360;

const AGENT_PANEL_TABLET_MIN_WIDTH = 420;

const AGENT_PANEL_MAX_WIDTH = 900;

const AGENT_PANEL_DEFAULT_WIDTH = 460;

const AGENT_PANEL_MOBILE_BREAKPOINT = 900;

const AGENT_PANEL_WIDTH_STORAGE_KEY = 'agent-side-panel-width';

// sessionStorage, not localStorage: the thread survives reloads in the same tab
// (deploy reloads, F5) but a new tab, window or browser restart starts fresh.

const AGENT_PANEL_WIDTH_VAR = '--agent-panel-width';

// Deep-link query param (e.g. from a share notification); consumed once.
const AGENT_CONVERSATION_DEEP_LINK_PARAM = 'agentConversation';

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const isPageReload = () =>
  (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)
    ?.type === 'reload';

const getMinimumAgentPanelWidth = () => {
  const viewportWidth = window.innerWidth;

  if (viewportWidth < AGENT_PANEL_MOBILE_BREAKPOINT) return viewportWidth;
  if (viewportWidth < 1200) return AGENT_PANEL_TABLET_MIN_WIDTH;

  return clamp(viewportWidth * 0.2, AGENT_PANEL_DESKTOP_MIN_WIDTH, 520);
};

const getMaximumAgentPanelWidth = () =>
  Math.max(getMinimumAgentPanelWidth(), Math.min(window.innerWidth * 0.72, AGENT_PANEL_MAX_WIDTH));

// Preferred opening width: wider than the floor so the panel is comfortable by
// default, but still clamped within the viewport-derived min/max bounds.
const getDefaultAgentPanelWidth = () =>
  clamp(AGENT_PANEL_DEFAULT_WIDTH, getMinimumAgentPanelWidth(), getMaximumAgentPanelWidth());

export function AgentPanelProvider({ children }: Readonly<React.PropsWithChildren>) {
  // Sits above the desktop window layer, where no router exists.
  const { location, navigate } = useShellNavigation();
  // On small screens v4 is the Agent chat only: always open, full screen, nothing to close to.
  const agentOnly = useAgentOnlyLayout();
  const { agent: agentPreferences } = useUserPreferences().preferences;
  const queryClient = useQueryClient();
  // Whether the column is open and the in-place history view live in one hook,
  // which also owns their persistence.
  const {
    isAgentPanelOpen,
    isHistoryOpen,
    setHistoryOpen,
    closeAgentPanel: closeAgentSurface,
    showAgentSidePanel,
  } = useAgentSurface(agentOnly);
  // Bumped on every ⌃F: the drawer focuses its search field in response, so a
  // repeat press while the drawer is already open still returns focus there.
  const [historySearchFocusKey, setHistorySearchFocusKey] = useState(0);
  const focusHistorySearch = useCallback(() => {
    setHistoryOpen(true);
    setHistorySearchFocusKey((current) => current + 1);
  }, [setHistoryOpen]);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  // Prefill for entry points that seed the composer (e.g. home quick-start
  // chips). Declarative so it survives opening a not-yet-mounted panel.
  const [agentPanelDraftSeed, setAgentPanelDraftSeed] = useState<AgentDraftSeed>();
  // Single handle to the mounted experience (shared with AgentSidePanel). Drives
  // the command palette, Ctrl+N, and focus without register/nonce indirection.
  const experienceRef = useRef<AgentExperienceHandle | null>(null);
  const focusAgentInput = useCallback(() => experienceRef.current?.focusInput(), []);
  // The active conversation is kept per browser tab so coming back later starts
  // a new thread instead of piling unrelated questions into an old one (past
  // threads stay in history). One experience instance backs both surface sizes,
  // so the id is simply its current thread. A tab opened by `window.open` starts
  // with a copy of its opener's sessionStorage, so drop the id there unless the
  // tab itself is reloading (otherwise a later F5 would restore the opener's thread).
  // Only on the tab's first page: `window.opener` stays set across later full-page
  // navigations, when the id is the tab's own thread.
  // Agent data in this tab belongs to one account; another account signing in clears it first.
  const account = useAuth().user?.email;
  const [agentConversationId, setStoredAgentConversationId] = useState<string | undefined>(() => {
    claimAgentTabSession(account);
    if (window.opener && window.history.length === 1 && !isPageReload()) {
      sessionStorage.removeItem(AGENT_CONVERSATION_ID_STORAGE_KEY);
      return undefined;
    }
    return sessionStorage.getItem(AGENT_CONVERSATION_ID_STORAGE_KEY) || undefined;
  });
  const setAgentConversationId = useCallback((conversationId: string | undefined) => {
    if (conversationId) sessionStorage.setItem(AGENT_CONVERSATION_ID_STORAGE_KEY, conversationId);
    else sessionStorage.removeItem(AGENT_CONVERSATION_ID_STORAGE_KEY);
    setStoredAgentConversationId(conversationId);
  }, []);
  useEffect(() => {
    if (claimAgentTabSession(account)) setStoredAgentConversationId(undefined);
  }, [account]);
  const [agentPanelWidth, setAgentPanelWidth] = useLocalStorage(
    AGENT_PANEL_WIDTH_STORAGE_KEY,
    getDefaultAgentPanelWidth,
    Number.isFinite,
  );

  const constrainedAgentPanelWidth = clamp(
    agentPanelWidth,
    getMinimumAgentPanelWidth(),
    getMaximumAgentPanelWidth(),
  );
  const closeAgentPanel = useCallback(() => {
    markAgentActivity();
    closeAgentSurface();
  }, [closeAgentSurface]);
  // Settings › Agent › Start a new conversation when I open the agent (unless it's recent).
  const startFreshIfStale = useCallback(() => {
    if (!agentPreferences.newConversationOnOpen || !agentConversationId) return;
    const lastActivity = getLastAgentActivity(queryClient, agentConversationId);
    if (isRecentActivity(lastActivity, agentPreferences.keepRecentFor)) return;
    if (experienceRef.current) experienceRef.current.newChat();
    else setAgentConversationId(undefined);
  }, [agentConversationId, agentPreferences, queryClient, setAgentConversationId]);
  // Showing a specific or new conversation leaves the in-place history view, which would
  // otherwise keep covering it (and its composer) after the switch.
  const showConversation = useCallback(() => {
    setHistoryOpen(false);
    showAgentSidePanel();
  }, [setHistoryOpen, showAgentSidePanel]);
  const openAgentPanel = useCallback(
    (conversationId?: string) => {
      if (conversationId) {
        setAgentConversationId(conversationId);
        showConversation();
        return;
      }
      if (!isAgentPanelOpen) startFreshIfStale();
      showAgentSidePanel();
    },
    [
      isAgentPanelOpen,
      showAgentSidePanel,
      showConversation,
      setAgentConversationId,
      startFreshIfStale,
    ],
  );
  const toggleAgentPanel = useCallback(
    () => (isAgentPanelOpen ? closeAgentPanel() : openAgentPanel()),
    [closeAgentPanel, isAgentPanelOpen, openAgentPanel],
  );

  const startAgentConversation = useCallback(
    (prompt: string) => {
      const conversationId = crypto.randomUUID();

      setAgentConversationId(conversationId);
      setAgentPanelDraftSeed(undefined);
      showConversation();
      experienceRef.current?.sendPrompt(conversationId, prompt);
    },
    [setAgentConversationId, showConversation],
  );

  const openNewAgentConversation = useCallback(
    (draftText?: string) => {
      if (draftText) {
        // Declarative prefill: seed the composer via a prop so it lands even when
        // the (lazy) experience isn't mounted yet — e.g. a home quick-start chip
        // opening the panel for the first time.
        setAgentConversationId(undefined);
        setAgentPanelDraftSeed((seed) => ({ key: (seed?.key ?? 0) + 1, text: draftText }));
        showConversation();

        return;
      }

      // Plain new chat: defer to the mounted experience's full reset (messages,
      // input, queued prompts, running turn), which also clears the conversation
      // id via onConversationChange(undefined). Falls back to dropping the id when
      // no experience is mounted (provider-only mode).
      showConversation();

      const experience = experienceRef.current;

      if (experience) experience.newChat();
      else setAgentConversationId(undefined);
    },
    [setAgentConversationId, showConversation],
  );

  const [voiceShortcutHeld, setVoiceShortcutHeld] = useState(false);
  useAgentShortcuts({
    startVoiceShortcut: () => {
      showConversation();
      setVoiceShortcutHeld(true);
    },
    endVoiceShortcut: (cancelled) => {
      if (cancelled) experienceRef.current?.cancelVoiceNote();
      setVoiceShortcutHeld(false);
    },
    tapVoiceShortcut: () => experienceRef.current?.finishVoiceNote() ?? false,
    isAgentPanelOpen,
    focusAgentInput,
    openCommandPalette: () => setCommandPaletteOpen(true),
    openNewAgentConversation,
    toggleHistory: () => setHistoryOpen(!isHistoryOpen),
    focusHistorySearch,
    toggleAgentPanel,
  });
  // Closing the column hands the keyboard back to the focused window.
  useEffect(() => {
    if (!isAgentPanelOpen) releaseAgentPanelKeyboard();
  }, [isAgentPanelOpen]);

  const pageContext = useMemo(
    () => getAgentPageContext({ pathname: location.pathname, search: location.search }),
    [location.pathname, location.search],
  );

  // Resizing writes straight to a CSS variable on the panel node: a state update
  // per pointermove would mean a synchronous localStorage write, a re-render of
  // the whole agent subtree, and a fresh emotion class for every pixel. The
  // dragged value is committed once, on release.
  const reserveRight = useDesktopLayout()?.reserveRight;
  const agentPanelRef = useRef<HTMLDivElement | null>(null);
  // The panel lives in a Portal, which mounts a commit after this provider: keep the node in
  // state so the reservation below runs once it exists (e.g. a panel restored open on reload).
  const [agentPanelNode, setAgentPanelNode] = useState<HTMLDivElement | null>(null);
  const attachAgentPanel = useCallback((node: HTMLDivElement | null) => {
    agentPanelRef.current = node;
    setAgentPanelNode(node);
  }, []);
  // While dragging, the desktop follows a CSS variable instead of React state (one commit on
  // release), so the drag never re-renders the desktop and its windows.
  const resizingRef = useRef(false);
  // Below `md` the column is a full-width overlay: it covers the desktop instead of narrowing it,
  // so reserving its width would leave a ~1px desktop (and windows fitted down to it).
  const isOverlay = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const isOverlayRef = useRef(isOverlay);
  isOverlayRef.current = isOverlay;
  useLayoutEffect(() => {
    if (!reserveRight) return;
    const node = agentPanelNode;
    const reserve = () => {
      if (resizingRef.current) return;
      reserveRight(isAgentPanelOpen && !isOverlay && node ? node.getBoundingClientRect().width : 0);
    };
    reserve();
    const observer =
      typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(reserve);
    if (node) observer?.observe(node);
    return () => {
      observer?.disconnect();
      reserveRight(0);
    };
    // The observer follows width changes (resize drags, viewport changes).
  }, [reserveRight, isAgentPanelOpen, isOverlay, agentPanelNode]);
  const draggedAgentPanelWidthRef = useRef<number | undefined>(undefined);
  const handleAgentPanelPointerMove = useCallback((event: globalThis.PointerEvent) => {
    // An overlay covers the desktop; a drag must not narrow it (the grip is hidden there too).
    if (isOverlayRef.current) return;
    const nextWidth = clamp(
      window.innerWidth - event.clientX,
      getMinimumAgentPanelWidth(),
      getMaximumAgentPanelWidth(),
    );

    if (!resizingRef.current) {
      resizingRef.current = true;
      setDesktopResizing(true);
    }
    draggedAgentPanelWidthRef.current = nextWidth;
    agentPanelRef.current?.style.setProperty(AGENT_PANEL_WIDTH_VAR, `${nextWidth}px`);
    setReservedRightVar(nextWidth);
  }, []);
  const handleAgentPanelResizeEnd = useCallback(() => {
    const draggedWidth = draggedAgentPanelWidthRef.current;

    draggedAgentPanelWidthRef.current = undefined;
    resizingRef.current = false;
    setDesktopResizing(false);
    if (draggedWidth === undefined || isOverlayRef.current) return;
    setAgentPanelWidth(draggedWidth);
    reserveRight?.(agentPanelRef.current?.getBoundingClientRect().width ?? draggedWidth);
  }, [reserveRight, setAgentPanelWidth]);
  const handleAgentPanelResizeStart = useHorizontalResize(
    handleAgentPanelPointerMove,
    handleAgentPanelResizeEnd,
  );

  // Open the panel on a deep-linked conversation, then strip the param.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const target = params.get(AGENT_CONVERSATION_DEEP_LINK_PARAM);

    if (!target) return;
    setAgentConversationId(target);
    showConversation();
    params.delete(AGENT_CONVERSATION_DEEP_LINK_PARAM);

    const nextSearch = params.toString();

    navigate({ pathname: location.pathname, search: nextSearch }, { replace: true });
  }, [location.search, location.pathname, navigate, setAgentConversationId, showConversation]);

  const { desktopApiRef, workspaceValue } = useAgentWorkspaceValue(agentConversationId);
  const { status: launcherStatus, reportRunning } = useAgentLauncherStatus(isAgentPanelOpen);
  const launcherValue = useMemo(
    () => ({ status: launcherStatus, hidden: isAgentPanelOpen, onOpen: () => openAgentPanel() }),
    [isAgentPanelOpen, launcherStatus, openAgentPanel],
  );

  const contextValue = useMemo(
    () => ({
      closeAgentPanel,
      isAgentPanelOpen,
      isHistoryOpen,
      setHistoryOpen,
      historySearchFocusKey,
      focusHistorySearch,
      focusAgentInput,
      startAgentConversation,
      openAgentPanel,
      openNewAgentConversation,
      toggleAgentPanel,
    }),
    [
      isAgentPanelOpen,
      closeAgentPanel,
      isHistoryOpen,
      setHistoryOpen,
      historySearchFocusKey,
      focusHistorySearch,
      focusAgentInput,
      startAgentConversation,
      openAgentPanel,
      openNewAgentConversation,
      toggleAgentPanel,
    ],
  );

  return (
    // App windows render inside, so their scopes nest in the Agent's and win shared keys.
    <HotkeysProvider scope={AGENT_HOTKEY_SCOPE}>
      <AgentPanelContext.Provider value={contextValue}>
        <AgentLauncherContext value={launcherValue}>
          {/* No desktop on small screens, so no workspace window to expand artifacts into. */}
          <AgentWorkspaceContext value={agentOnly ? null : workspaceValue}>
            <AgentDesktopBridge apiRef={desktopApiRef} />
            {children}
            {commandPaletteOpen && (
              // Quick Find owns the keyboard while open; global shortcuts (⌘,) still work.
              <HotkeysProvider scope="quick-find" exclusive={{ allow: ['global'] }}>
                <Suspense fallback={null}>
                  <QuickFind
                    onClose={() => setCommandPaletteOpen(false)}
                    onAskAgent={startAgentConversation}
                    onOpenConversation={openAgentPanel}
                  />
                </Suspense>
              </HotkeysProvider>
            )}
            {/* The top-bar positions render in the shell chrome; this is the floating one. */}
            <AgentLauncherChip position="bottom-right" />
            <Portal>
              <DesktopShellRouter>
                <Box
                  ref={attachAgentPanel}
                  data-agent-panel
                  style={
                    {
                      [AGENT_PANEL_WIDTH_VAR]: `${constrainedAgentPanelWidth}px`,
                    } as React.CSSProperties
                  }
                  // No surface of its own: the column sits on the desktop aurora (which spans the
                  // viewport) while the desktop content reflows into the space left of it.
                  sx={(theme) => ({
                    position: 'fixed',
                    top: agentOnly ? 0 : 48,
                    right: 0,
                    bottom: 0,
                    left: 'auto',
                    zIndex: theme.zIndex.appBar - 1,
                    transform: isAgentPanelOpen ? 'translateX(0)' : 'translateX(100%)',
                    opacity: isAgentPanelOpen ? 1 : 0,
                    transition: theme.transitions.create(['transform', 'opacity'], {
                      easing: theme.transitions.easing.easeOut,
                      duration: theme.transitions.duration.enteringScreen,
                    }),
                    [theme.breakpoints.down('md')]: { bgcolor: 'background.default' },
                    width: { xs: '100vw', md: `var(${AGENT_PANEL_WIDTH_VAR})` },
                    minWidth: { md: getMinimumAgentPanelWidth() },
                    maxWidth: { md: getMaximumAgentPanelWidth() },
                  })}
                >
                  <Box inert={!isAgentPanelOpen} sx={{ height: '100%', minHeight: 0 }}>
                    <Suspense fallback={null}>
                      <AgentSidePanel
                        active={isAgentPanelOpen}
                        voiceShortcutHeld={voiceShortcutHeld}
                        conversationId={agentConversationId}
                        draftSeed={agentPanelDraftSeed}
                        experienceRef={experienceRef}
                        pageContext={pageContext}
                        onConversationChange={setAgentConversationId}
                        onClose={agentOnly ? undefined : closeAgentPanel}
                        onRunningChange={reportRunning}
                        onResizeStart={handleAgentPanelResizeStart}
                      />
                    </Suspense>
                  </Box>
                </Box>
              </DesktopShellRouter>
            </Portal>
          </AgentWorkspaceContext>
        </AgentLauncherContext>
      </AgentPanelContext.Provider>
    </HotkeysProvider>
  );
}
